/**
 * workedExampleAdoption — the retry-safe "Open a worked example" adoption step
 * (onboarding Beat-0, plan `.plan/features/2026-07-02-onboarding-guided-first-recipe.md`).
 *
 * Double-copy guard: the empty-library `WorkedExampleAction` bounds its spin-off
 * with a 15 s client timeout (`withTimeout`). If the server LANDED the seed
 * adoption but the client timed out first, the user sees an inline error and
 * retries — and a naive second `recipe.spinOff` would mint a SECOND
 * slug-deduped copy of the seed ("napoletana-pizza-dough-2"). Before spinning
 * off, we do one cheap `recipe.list` read (raw client, so a stale-empty SWR
 * cache from the pre-timeout render can't hide the freshly-created copy) and
 * look for a recipe already adopted from the seed. `spinOffFromSeed`
 * (apps/api/src/routes/recipes/spin-off.ts) stamps
 * `fork_upstream_recipe_uuid = seed.uuid` (= the seed's public UUID), which is
 * the reliable marker. If one exists we open THAT instead of re-adopting.
 */

import { NAPOLETANA_SEED_PUBLIC_UUID } from "./workedExampleSeed";

/** The raw `recipe` client surface this step needs (list + spinOff). */
export interface WorkedExampleRecipeClient {
  recipe: {
    list: (input: { limit: number }) => Promise<{
      recipes: Array<{ uuid: string; fork_upstream_recipe_uuid?: string | null }>;
    }>;
    spinOff: (input: { sourcePublicUuid: string }) => Promise<{ uuid: string; slug: string }>;
  };
}

/** Outcome of the adoption step. `created` is false when an existing copy was opened. */
export interface WorkedExampleAdoption {
  uuid: string;
  /** true → a fresh spin-off was minted; false → an existing seed copy was reused. */
  created: boolean;
}

/**
 * Resolve the recipe to open for "Open a worked example", adopting the seed at
 * most once even across a timed-out retry.
 *
 * Never blocks on the pre-check: a failed `recipe.list` falls through to the
 * spin-off (the worst case is the original double-copy risk, not a hard failure
 * of the user's action). The spin-off itself is NOT caught here — its failure
 * propagates so the caller (WorkedExampleAction, Rule 8) surfaces it.
 */
export async function adoptWorkedExample(
  client: WorkedExampleRecipeClient,
): Promise<WorkedExampleAdoption> {
  try {
    const { recipes } = await client.recipe.list({ limit: 100 });
    const existing = recipes.find(
      (r) => r.fork_upstream_recipe_uuid === NAPOLETANA_SEED_PUBLIC_UUID,
    );
    if (existing) return { uuid: existing.uuid, created: false };
  } catch (err) {
    // Pre-check failure must never block the primary action.
    console.warn("[proof.onboarding] worked-example adoption pre-check failed", { err });
  }

  const result = await client.recipe.spinOff({ sourcePublicUuid: NAPOLETANA_SEED_PUBLIC_UUID });
  return { uuid: result.uuid, created: true };
}

/**
 * Brand-voice toast copy for the "Open a worked example" success path. Branches
 * on the adoption outcome so the reuse case (the double-copy guard reopened an
 * EXISTING seed copy) does not falsely claim a fresh recipe was created:
 *   - created → the governed first-recipe line, matching a fresh create.
 *   - reused  → a quiet line that states what happened (the existing copy opened).
 *
 * branding.md §5 — precise, quiet, no exclamations, no banned words.
 */
export function workedExampleToastMessage(created: boolean): string {
  return created ? "Recipe created." : "Opened your worked example.";
}
